import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ClientContacts = ({ clientId, onContactAdded }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showInactiveContacts, setShowInactiveContacts] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile_phone: '',
    title: '',
    is_primary: false,
    username: '',
    password: '',
    status: 'active'
  });

  useEffect(() => {
    fetchContacts();
  }, [clientId]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/clients/${clientId}/contacts`);
      console.log('Contacts response:', response.data);
      setContacts(response.data.contacts || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      let response;
      if (editingContactId) {
        // Update existing contact
        response = await api.put(`/clients/${clientId}/contacts/${editingContactId}`, contactForm);
        console.log('Contact updated:', response.data);
        
        // Update the contact in the list
        setContacts(prev => prev.map(contact => 
          contact.id === editingContactId ? response.data.contact : contact
        ));
        
        // Reset edit mode
        setShowEditForm(false);
        setEditingContactId(null);
      } else {
        // Add new contact
        response = await api.post(`/clients/${clientId}/contacts`, contactForm);
        console.log('Contact added:', response.data);
        
        // Add the new contact to the list
        setContacts(prev => [...prev, response.data.contact]);
        
        // Hide add form
        setShowAddForm(false);
        
        // Notify parent component if callback provided
        if (onContactAdded) {
          onContactAdded(response.data.contact);
        }
      }
      
      // Reset form
      setContactForm({
        name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile_phone: '',
        title: '',
        is_primary: false,
        username: '',
        password: '',
        status: 'active'
      });
      
      // Reset password visibility
      setShowPassword(false);
    } catch (err) {
      console.error('Error saving contact:', err);
      setError('Failed to save contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (contact) => {
    // Set form values to the contact being edited
    setContactForm({
      name: contact.name,
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile_phone: contact.mobile_phone || '',
      title: contact.title || '',
      is_primary: contact.is_primary,
      username: contact.username || '',
      password: '', // Don't populate password field for security reasons
      status: contact.status
    });
    
    // Set edit mode
    setEditingContactId(contact.id);
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingContactId(null);
    setContactForm({
      name: '',
      last_name: '',
      email: '',
      phone: '',
      mobile_phone: '',
      title: '',
      is_primary: false,
      username: '',
      password: '',
      status: 'active'
    });
    setShowPassword(false);
  };

  const handleSetPrimary = async (contactId) => {
    try {
      setLoading(true);
      await api.put(`/clients/${clientId}/contacts/${contactId}/primary`);
      
      // Update contacts list to reflect new primary contact
      setContacts(prev => prev.map(contact => ({
        ...contact,
        is_primary: contact.id === contactId
      })));
    } catch (err) {
      console.error('Error setting primary contact:', err);
      setError('Failed to update primary contact.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      setLoading(true);
      await api.delete(`/clients/${clientId}/contacts/${contactId}`);
      
      // Remove the deleted contact from the list
      setContacts(prev => prev.filter(contact => contact.id !== contactId));
      
      // If we were editing this contact, cancel edit mode
      if (editingContactId === contactId) {
        handleCancelEdit();
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError('Failed to delete contact.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Filter contacts based on status visibility preference
  const filteredContacts = showInactiveContacts 
    ? contacts 
    : contacts.filter(contact => contact.status === 'active');

  if (loading && contacts.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md border-l-4 border-red-500">{error}</div>
      )}
      
      {/* Header Section - Enhanced with Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 bg-indigo-50">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h2 className="text-xl font-semibold text-indigo-900">Contacts</h2>
              <p className="mt-2 text-sm text-indigo-700">
                Contacts associated with this client
              </p>
              <p className="mt-1 text-sm text-indigo-600">
                Client Login: <a href="https://app.theframeworx.io/client-login" className="underline hover:text-indigo-800 font-medium" target="_blank" rel="noopener noreferrer">app.theframeworx.io/client-login</a>
              </p>
            </div>
            {!showAddForm && !showEditForm && (
              <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Add Contact
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal Overlay for Contact Form */}
      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
            onClick={showEditForm ? handleCancelEdit : () => setShowAddForm(false)}
          />
          
          {/* Modal panel */}
          <div className="bg-white relative rounded-xl shadow-xl transform transition-all sm:max-w-lg sm:w-full">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                onClick={showEditForm ? handleCancelEdit : () => setShowAddForm(false)}
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            
            <div className="p-8 bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800 mb-2 pb-3">
                {showEditForm ? 'Edit Contact Information' : 'Add New Contact'}
              </h3>
              <p className="text-sm text-gray-500 mb-6">Complete the form fields below. Required fields are marked with an asterisk (*).</p>
              
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Details Section - Indigo Theme */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
                  <div className="px-4 py-3 bg-indigo-50">
                    <h4 className="text-base font-medium text-indigo-900">
                      Personal Details
                    </h4>
                    <p className="mt-1 text-sm text-indigo-700">Basic contact information and personal details.</p>
                  </div>
                  <div className="border-t border-indigo-100 px-4 py-4">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          required
                          value={contactForm.name}
                          onChange={handleInputChange}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          id="last_name"
                          value={contactForm.last_name}
                          onChange={handleInputChange}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          id="title"
                          value={contactForm.title}
                          onChange={handleInputChange}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          value={contactForm.email}
                          onChange={handleInputChange}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          id="phone"
                          value={contactForm.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          pattern="^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$"
                          title="Phone number format: (123) 456-7890"
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                        <p className="mt-1 text-xs text-gray-400">Format: (123) 456-7890</p>
                      </div>

                      <div>
                        <label htmlFor="mobile_phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile Phone
                        </label>
                        <input
                          type="tel"
                          name="mobile_phone"
                          id="mobile_phone"
                          value={contactForm.mobile_phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          pattern="^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$"
                          title="Phone number format: (123) 456-7890"
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                        <p className="mt-1 text-xs text-gray-400">Format: (123) 456-7890</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Portal Access Section - Blue Theme */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-blue-500">
                  <div className="px-4 py-3 bg-blue-50">
                    <h4 className="text-base font-medium text-blue-900">
                      Portal Access
                    </h4>
                    <p className="mt-1 text-sm text-blue-700">Login credentials and system access settings.</p>
                  </div>
                  <div className="border-t border-blue-100 px-4 py-4">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                          Username
                        </label>
                        <input
                          type="text"
                          name="username"
                          id="username"
                          value={contactForm.username}
                          onChange={handleInputChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                          Password {showEditForm && "(leave blank to keep current)"}
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            id="password"
                            value={contactForm.password}
                            onChange={handleInputChange}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md pr-10"
                          />
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-600"
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                              <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          name="status"
                          id="status"
                          value={contactForm.status}
                          onChange={handleInputChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      
                      {/* Primary Contact Toggle */}
                      <div className="mt-6">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_primary"
                            checked={contactForm.is_primary}
                            onChange={handleInputChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          <span className="ml-3 text-sm font-medium text-gray-700">Primary Contact</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-between space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    className="rounded-md bg-gray-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    onClick={showEditForm ? handleCancelEdit : () => setShowAddForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    {showEditForm ? 'Save Changes' : 'Add Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact List */}
      {contacts.length > 0 ? (
        <div className="mt-6">
          {/* Filter Toggle - Enhanced with Indigo Theme */}
          <div className="mb-4 flex items-center bg-white p-4 rounded-lg shadow border-l-4 border-indigo-200">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showInactiveContacts}
                onChange={(e) => setShowInactiveContacts(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                Show inactive contacts
              </span>
            </label>
            <span className="ml-4 text-sm text-indigo-600 font-medium">
              ({filteredContacts.length} of {contacts.length} contacts shown)
            </span>
          </div>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    NAME
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    TITLE
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    EMAIL
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    PHONE
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    MOBILE PHONE
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    USERNAME
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    STATUS
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    PRIMARY
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {contact.name} {contact.last_name && contact.last_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.title || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-indigo-600 hover:text-indigo-900">
                          {contact.email}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.phone ? (
                        <a href={`tel:${contact.phone}`} className="text-indigo-600 hover:text-indigo-900">
                          {contact.phone}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.mobile_phone ? (
                        <a href={`tel:${contact.mobile_phone}`} className="text-indigo-600 hover:text-indigo-900">
                          {contact.mobile_phone}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.username || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        contact.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {contact.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.is_primary ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          Primary
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetPrimary(contact.id)}
                          className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
                        >
                          Make Primary
                        </button>
                      )}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div className="flex items-center space-x-3 justify-end">
                        <button
                          onClick={() => handleEdit(contact)}
                          className="text-gray-400 hover:text-indigo-600 transition-colors"
                          title="Edit contact"
                        >
                          <PencilIcon className="h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">Edit contact</span>
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete contact"
                        >
                          <TrashIcon className="h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">Delete contact</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white mt-6 border border-gray-200 rounded-lg shadow">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
            <svg
              className="h-6 w-6 text-indigo-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-sm font-medium text-gray-900">
            {contacts.length === 0 ? 'No contacts' : 'No active contacts'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {contacts.length === 0 
              ? "This client doesn't have any contacts yet."
              : "This client has contacts, but they are all inactive. Toggle 'Show inactive contacts' to view them."
            }
          </p>
          {contacts.length === 0 && (
            <div className="mt-6">
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Contact
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientContacts; 